import { PWAUpdateTestMode, type PWAUpdatePolicy } from './pwa-update-policy'

export type DevTestPolicyOverrides = Pick<Partial<PWAUpdatePolicy>, 'snoozeMinutes' | 'deadlineMinutes'>

const TEST_PARAM = 'pwaUpdateTest'
const TEST_SNOOZE_MINUTES_PARAM = 'pwaUpdateSnoozeMinutes'
const TEST_DEADLINE_MINUTES_PARAM = 'pwaUpdateDeadlineMinutes'
const RELOAD_PARAM = 'pwaReload'

export const getPWAUpdateStorageKey = (version: string, key: string) => `pwa.update.${version}.${key}`

/**
 * Non-production update simulator: append
 * `?pwaUpdateTest=normal&pwaUpdateSnoozeMinutes=1&pwaUpdateDeadlineMinutes=10`
 * to an app URL to test a one-minute snooze and the blocking update prompt after ten minutes.
 */
export const getDevTestMode = (): PWAUpdateTestMode | null => {
  if (import.meta.env.MODE === 'production') return null

  const mode = new URLSearchParams(window.location.search).get(TEST_PARAM)
  if (mode !== PWAUpdateTestMode.Normal && mode !== PWAUpdateTestMode.Critical && mode !== PWAUpdateTestMode.CriticalNow) return null

  return mode
}

const getUrlWithoutDevTestParams = () => {
  const nextUrl = new URL(window.location.href)
  nextUrl.searchParams.delete(TEST_PARAM)
  nextUrl.searchParams.delete(TEST_SNOOZE_MINUTES_PARAM)
  nextUrl.searchParams.delete(TEST_DEADLINE_MINUTES_PARAM)
  return nextUrl.toString()
}

export const removeOneShotReloadParamFromHistory = () => {
  const nextUrl = new URL(window.location.href)
  if (!nextUrl.searchParams.has(RELOAD_PARAM)) return

  nextUrl.searchParams.delete(RELOAD_PARAM)
  window.history.replaceState(window.history.state, '', nextUrl.toString())
}

export const removeDevTestParamsFromHistory = () => {
  window.history.replaceState(window.history.state, '', getUrlWithoutDevTestParams())
}

export const reloadWithoutDevTestParams = () => {
  window.location.replace(getUrlWithoutDevTestParams())
}

export const reloadForActivatedUpdate = () => {
  const nextUrl = new URL(getUrlWithoutDevTestParams())
  nextUrl.searchParams.set(RELOAD_PARAM, String(Date.now()))
  window.location.replace(nextUrl.toString())
}

export const formatClock = (ms: number) => {
  const seconds = Math.max(0, Math.ceil(ms / 1000))
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  return `${minutes}:${String(remainingSeconds).padStart(2, '0')}`
}

export const formatCountdownBadge = (ms: number) => {
  if (ms < 60 * 1000) return `${Math.max(0, Math.ceil(ms / 1000))}s`

  const minutes = Math.ceil(ms / 60000)
  if (minutes < 60) return `${minutes}m`

  return `${Math.floor(minutes / 60)}h`
}

const getPositiveQueryNumber = (params: URLSearchParams, key: string): number | undefined => {
  const value = Number(params.get(key))
  return Number.isFinite(value) && value > 0 ? value : undefined
}

export const getDevTestPolicyOverrides = (params: URLSearchParams): DevTestPolicyOverrides => {
  const snoozeMinutes = getPositiveQueryNumber(params, TEST_SNOOZE_MINUTES_PARAM)
  const deadlineMinutes = getPositiveQueryNumber(params, TEST_DEADLINE_MINUTES_PARAM)

  return {
    ...(snoozeMinutes ? { snoozeMinutes } : {}),
    ...(deadlineMinutes ? { deadlineMinutes } : {}),
  }
}
